import axios from 'axios'

import { client } from '@/lib/api/client'
import {
  HISTORY_PAGE_SIZE,
  venueOperationVerificationApplicationsPath,
  venueOperationVerificationPath,
  venueOperationVerificationPrefillPath,
  venueOperationVerificationUploadUrlPath,
} from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'
import { attachApiResponseLogger } from '@/lib/api/responseLogger'

import type {
  OperationVerificationApplicationsPage,
  OperationVerificationPrefill,
  OperationVerificationSubmitInput,
  OperationVerificationUploadUrlInput,
  OperationVerificationUploadUrlResult,
  OperationVerificationView,
} from './operationVerificationTypes'

/**
 * 운영 인증 신청·서류 3EP + 오브젝트 스토리지 직접 업로드(`#771`, design.md §2 자율 결정 4).
 *
 * 🚨 서류 업로드(`uploadOperationVerificationDocument`)는 `client`(WAS 인증 인스턴스)를 쓰지 않는다 —
 * signed `PUT` 은 `requiredHeaders` 가 서명에 포함돼 있어, `client` 인터셉터가 강제로 붙이는
 * `Authorization`·`Content-Type: application/json` 이 서명과 충돌한다(`venueImageUploadApi.ts` 의
 * `uploadClient` 와 같은 격리 이유).
 *
 * 🚨 **저장소는 이 파일의 관심사가 아니다**(`#892` — S3 → Firebase Storage(GCS) 전환). 업로드 대상은
 * EP-2 가 준 `uploadUrl` 이 전부이고, 프런트는 `documentKey` 를 **불투명 값**으로만 다룬다
 * (허브 api-spec §1 *"documentKey 를 파싱·조립하지 말 것"*). 그래서 전환 후에도 이 파일의 로직은
 * 바뀌지 않는다 — 바뀐 것은 `uploadUrl` 문자열의 호스트뿐이다.
 */

/** 서류 업로드 실패 전용 에러 — `VenueImageUploadError` 와 같은 톤(호출부가 하나의 catch 로 처리한다). */
export class OperationVerificationDocumentUploadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'OperationVerificationDocumentUploadError'
  }
}

/** 대상 호스트(오브젝트 스토리지)가 달라 `client` 와 분리한다 — 인증 헤더를 주입하지 않는다. */
const uploadClient = axios.create({ timeout: 30_000 })

attachApiResponseLogger(uploadClient)

/** EP-3 — 직전 신청 값 조회(재신청 prefill). 신청 이력이 없어도 200. */
export async function fetchOperationVerificationPrefill(
  venueId: string
): Promise<OperationVerificationPrefill> {
  try {
    const { data } = await client.get<OperationVerificationPrefill>(
      venueOperationVerificationPrefillPath(venueId)
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/** EP-2 — 인증 서류 업로드 URL 발급. */
export async function requestOperationVerificationUploadUrl(
  venueId: string,
  input: OperationVerificationUploadUrlInput
): Promise<OperationVerificationUploadUrlResult> {
  try {
    const { data } = await client.post<OperationVerificationUploadUrlResult>(
      venueOperationVerificationUploadUrlPath(venueId),
      input
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * 업로드 실패의 원인을 콘솔에 구분해 남긴다(`#891` 설계 §자율 결정 6).
 *
 * 🚨 **응답이 없는 실패(CSP `connect-src` 차단·S3 CORS 거부·네트워크 단절)는 지금까지 아무 흔적도
 * 남기지 않았다** — `attachApiResponseLogger` 가 `error.response` 가 있을 때만 로그하기 때문이다.
 * 그 침묵이 "업로드 경로가 잘못 구현됐다"는 오진(`#891` 원 이슈)을 만든 직접적 원인이라, 이 한 곳만은
 * 배포 모드와 무관하게(= dev/local 게이팅 없이) 남긴다. 실패했을 때만 찍히므로 상시 소음이 아니다.
 *
 * 🔒 signed URL 의 query string(S3 `X-Amz-Signature` · GCS `X-Goog-Signature`)은 절대 남기지 않는다 —
 * `responseLogger` 와 같은 규칙. `split('?')[0]` 이 저장소와 무관하게 서명을 통째로 잘라낸다.
 */
function logDocumentUploadFailure(uploadUrl: string, error: unknown): void {
  const endpoint = uploadUrl.split('?')[0]

  if (axios.isAxiosError(error) && error.response) {
    console.error(`[서류 업로드 실패] ${endpoint} — HTTP ${error.response.status}`)
    return
  }

  console.error(
    `[서류 업로드 실패] ${endpoint} — 응답 없음(요청이 전송되지 않았거나 차단됨). ` +
      'CSP connect-src · 스토리지 버킷 CORS · 네트워크를 확인할 것.'
  )
}

/**
 * 스토리지 직접 `PUT` — EP-2 가 준 `uploadUrl`·`requiredHeaders` 를 그대로 쓴다.
 * 🚨 `requiredHeaders` 를 빠뜨리거나 다른 값을 보내면 스토리지가 403(서명 불일치) 을 준다 —
 * S3 도 GCS V4 도 `Content-Type` 을 **서명에 포함**하므로 이 규칙은 두 저장소에서 동일하다(`#892`).
 * 이때는 WAS 오류가 아니므로 사용자에게는 "다시 시도" 로 안내한다(api-spec §3).
 * 🚨 사용자 문구는 실패 원인과 무관하게 **하나로 유지**한다 — 원인 구분은 콘솔 진단으로만 노출한다
 * (사용자가 CSP·CORS 를 보고 할 수 있는 행동이 없다).
 */
export async function uploadOperationVerificationDocument(
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  file: File
): Promise<void> {
  try {
    await uploadClient.put(uploadUrl, file, { headers: requiredHeaders })
  } catch (error) {
    logDocumentUploadFailure(uploadUrl, error)
    throw new OperationVerificationDocumentUploadError(
      '서류를 업로드하지 못했습니다. 다시 시도해 주세요.'
    )
  }
}

/** EP-1 — 운영 인증 신청 제출. 응답은 `#770` EP-A 와 같은 shape(전환 후 상태). */
export async function submitOperationVerification(
  venueId: string,
  input: OperationVerificationSubmitInput
): Promise<OperationVerificationView> {
  try {
    const { data } = await client.post<OperationVerificationView>(
      venueOperationVerificationPath(venueId),
      input
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * EP-A — 운영 인증 상태 요약 조회(`#778`, 백엔드 `#770`+`#777`). 제출(`POST`)이 쓰는 것과 같은 경로다.
 * 🚨 게이팅 없음 — 인증 미완료·만료·반려·노출 중단 어느 상태에서도 200(인증하러 오는 입구다).
 */
export async function fetchOperationVerificationStatus(
  venueId: string
): Promise<OperationVerificationView> {
  try {
    const { data } = await client.get<OperationVerificationView>(
      venueOperationVerificationPath(venueId)
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * EP-2 — 운영 인증 이력(회차) 목록(`#778`, 백엔드 `#777` §3) — 커서 페이지네이션.
 * 🚨 `size`는 항상 `HISTORY_PAGE_SIZE`(20)로 고정한다 — 이 화면에 페이지 크기 선택 UI가 없다.
 * 🚨 신청한 적 없으면 `applications: []` + 200(404 아니다).
 */
export async function fetchOperationVerificationApplications(
  venueId: string,
  cursor?: string
): Promise<OperationVerificationApplicationsPage> {
  try {
    const { data } = await client.get<OperationVerificationApplicationsPage>(
      venueOperationVerificationApplicationsPath(venueId),
      { params: { cursor, size: HISTORY_PAGE_SIZE } }
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}
